import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Table,
} from "reactstrap"
import Swal from "sweetalert2"
import Modules from "./Modules"

const RoleModal = ({
  isOpen,
  toggle,
  roleData,
  onChange,
  onSave,
  type = "",
}) => {
  const [modulesState, setModulesState] = useState([])

  useEffect(() => {
    if (roleData?.permissions) {
      const permission = JSON.parse(roleData.permissions)

      const permissions = Modules.map((per, index) => {
        const perm = permission[index]?.split(",") || []

        return {
          ...per,
          viewglobal: Number(perm[1] || 0),
          view: Number(perm[2] || 0),
          add: Number(perm[3] || 0),
          edit: Number(perm[4] || 0),
          delete: Number(perm[5] || 0),
        }
      })

      setModulesState(permissions)
    } else {
      setModulesState(Modules)
    }
  }, [roleData])

  const handleToggle = (moduleId, field) => {
    const module = modulesState.find(m => m.id === moduleId)
    if (!module) return
    const moduleName = module.module

    switch (field) {
      case "viewglobal":
        Swal.fire({
          title: `Toggle View Global for "${moduleName}"`,
          text:
            module.viewglobal === 0
              ? `Enabling View Global will automatically disable View. Add/Edit/Delete will be disabled.`
              : `Disabling View Global allows you to toggle View and other permissions.`,
          icon: "info",
          showCancelButton: true,
          confirmButtonText: "Yes",
        }).then(result => {
          if (result.isConfirmed) {
            const updatedModule = {
              ...module,
              viewglobal: module.viewglobal === 0 ? 1 : 0,
              view: module.viewglobal === 0 ? 0 : module.view,
              add: module.viewglobal === 0 ? 0 : module.add,
              edit: module.viewglobal === 0 ? 0 : module.edit,
              delete: module.viewglobal === 0 ? 0 : module.delete,
            }
            setModulesState(
              modulesState.map(m => (m.id === moduleId ? updatedModule : m))
            )
          }
        })
        break

      case "view":
        Swal.fire({
          title: `Toggle View for "${moduleName}"`,
          text:
            module.view === 0
              ? `Enabling View allows Add/Edit/Delete for this module. View Global will be disabled.`
              : `Disabling View will disable Add/Edit/Delete for this module.`,
          icon: "info",
          showCancelButton: true,
          confirmButtonText: "Yes",
        }).then(result => {
          if (result.isConfirmed) {
            const updatedModule = {
              ...module,
              view: module.view === 0 ? 1 : 0,
              viewglobal: module.view === 0 ? 0 : module.viewglobal,
              add: module.view === 0 ? module.add : 0,
              edit: module.view === 0 ? module.edit : 0,
              delete: module.view === 0 ? module.delete : 0,
            }
            setModulesState(prev =>
              prev.map(m => (m.id === moduleId ? updatedModule : m))
            )
          }
        })
        break

      case "add":
      case "edit":
      case "delete":
        Swal.fire({
          title: `Toggle ${field.toUpperCase()} for "${moduleName}"`,
          text:
            module[field] === 0
              ? `This will allow the user to ${field} records for "${moduleName}".`
              : `This will remove the ${field} permission for "${moduleName}".`,
          icon: "info",
          showCancelButton: true,
          confirmButtonText: "Yes",
        }).then(result => {
          if (result.isConfirmed) {
            const updatedModule = {
              ...module,
              [field]: module[field] === 0 ? 1 : 0,
            }
            setModulesState(prev =>
              prev.map(m => (m.id === moduleId ? updatedModule : m))
            )
          }
        })
        break

      default:
        break
    }
  }

  // Validation + Save Handler
  const handleSave = () => {
    const roleName = (roleData?.roleName || "").trim()

    if (!roleName) {
      Swal.fire({
        icon: "warning",
        title: "Role Name Required",
        text: "Please enter a role name before saving.",
      })
      return
    }

    // Proceed with save if validation passes
    onSave(modulesState)
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl">
      <ModalHeader toggle={toggle}>
        {type === "view"
          ? "View Role"
          : roleData?.id
          ? "Edit Role"
          : "Add Role"}
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>
            Role Name <span className="text-danger">*</span>
          </Label>
          <Input
            type="text"
            name="roleName"
            value={roleData?.roleName || ""}
            onChange={onChange}
            placeholder="Enter role name"
            disabled={type === "view"}
            invalid={!roleData?.roleName?.trim()} // Optional: visual red border
          />
        </FormGroup>

        <Table className="table table-bordered m-0 text-center mt-4 role-table">
          <thead>
            <tr>
              <th className="text-start">Module</th>
              <th>View Global</th>
              <th>View</th>
              <th>Add</th>
              <th>Update</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {modulesState.map(item => (
              <tr key={item.id}>
                <td
                  className={`text-start ${
                    !item.parentId ? "fw-bold" : "ps-5"
                  }`}
                >
                  {item.module}
                </td>

                {/* View Global Switch */}
                <td>
                  <div className="form-check form-switch d-flex justify-content-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={item.viewglobal === 1}
                      onChange={() => handleToggle(item.id, "viewglobal")}
                      disabled={type === "view"}
                    />
                  </div>
                </td>

                {/* View Switch */}
                <td>
                  <div className="form-check form-switch d-flex justify-content-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={item.view === 1}
                      onChange={() => handleToggle(item.id, "view")}
                      disabled={type === "view"}
                    />
                  </div>
                </td>

                {/* Add Switch */}
                <td>
                  {item.isAdd ? (
                    <div className="form-check form-switch d-flex justify-content-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={item.add === 1}
                        onChange={() => handleToggle(item.id, "add")}
                        disabled={type === "view"}
                      />
                    </div>
                  ) : (
                    "--"
                  )}
                </td>

                {/* Edit Switch */}
                <td>
                  {item.isEdit ? (
                    <div className="form-check form-switch d-flex justify-content-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={item.edit === 1}
                        onChange={() => handleToggle(item.id, "edit")}
                        disabled={type === "view"}
                      />
                    </div>
                  ) : (
                    "--"
                  )}
                </td>

                {/* Delete Switch */}
                <td>
                  {item.isDelete ? (
                    <div className="form-check form-switch d-flex justify-content-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={item.delete === 1}
                        onChange={() => handleToggle(item.id, "delete")}
                        disabled={type === "view"}
                      />
                    </div>
                  ) : (
                    "--"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ModalBody>

      {type === "" && (
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSave}>
            Save
          </Button>
          <Button className="btn-cancel" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      )}
    </Modal>
  )
}

export default RoleModal
